import { BasicModal, Typography } from "@/components";
import { cn } from "@/libraries/cn";
import { usePrompt } from "@/stores/prompt";
import Button from "@mui/material/Button";
import type { ButtonProps } from "@mui/material/Button";
import type { ModalProps } from "@mui/material/Modal";
import omit from "lodash/omit";
import { forwardRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/shallow";

export interface ModalPromptProps
	extends Partial<Omit<ModalProps, "children">> {
	title?: string;
	description?: string;
	positiveButtonProps?: ButtonProps & { label: string };
	negativeButtonProps?: ButtonProps & { label: string };
}

const ModalPrompt = forwardRef<HTMLDivElement, ModalPromptProps>(
	(
		{
			title: propsTitle,
			description: propsDescription,
			open,
			onClose,
			positiveButtonProps,
			negativeButtonProps,
			...props
		},
		ref,
	) => {
		const { t } = useTranslation("common");
		const closePrompt = usePrompt(({ close }) => close);
		const storeState = usePrompt(
			useShallow(
				({ isOpen, title, description, positiveButton, negativeButton }) => ({
					isOpen,
					title,
					description,
					positiveButton,
					negativeButton,
				}),
			),
		);
		const isOpen = open ?? storeState.isOpen;
		const title = propsTitle ?? storeState.title;
		const message = propsDescription ?? storeState.description;
		const pb = positiveButtonProps ?? storeState.positiveButton;
		const nb = negativeButtonProps ??
			storeState.negativeButton ?? { label: t("cancel") };

		const onConfirmCLick = useCallback(
			(e: React.MouseEvent<HTMLButtonElement>) => {
				pb?.onClick?.(e);
				if (storeState.isOpen) closePrompt();
			},
			[pb?.onClick, storeState.isOpen, closePrompt],
		);

		const onCloseCLick = useCallback(
			(e: React.MouseEvent<HTMLButtonElement>) => {
				nb?.onClick?.(e);
				// @ts-expect-error
				if (onClose) onClose();
				if (storeState.isOpen) closePrompt();
			},
			[onClose, nb?.onClick, storeState.isOpen, closePrompt],
		);

		return (
			<BasicModal isOpen={isOpen} onClose={onCloseCLick} {...props}>
				<div ref={ref} className="flex flex-col gap-4">
					<Typography fontWeight={600} fontSize={16} align="center">
						{title}
					</Typography>
					{message ? (
						<Typography fontWeight={400} fontSize={14} align="center">
							{message}
						</Typography>
					) : null}
					{pb?.label ? (
						<Button
							fullWidth
							variant="contained"
							color="primary"
							size="large"
							className={cn("!rounded-full", pb.className)}
							onClick={onConfirmCLick}
							{...omit(pb, ["label", "className", "onClick"])}
						>
							{pb.label}
						</Button>
					) : null}
					{nb?.label ? (
						<Button
							fullWidth
							variant="outlined"
							color="primary"
							size="large"
							className={cn("!rounded-full", nb.className)}
							onClick={onCloseCLick}
							{...omit(nb, ["label", "className", "onClick"])}
						>
							{nb.label}
						</Button>
					) : null}
				</div>
			</BasicModal>
		);
	},
);

ModalPrompt.displayName = "ModalPrompt";

export default ModalPrompt;
